import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function About() {
  usePageMeta({
    title: '关于我们 - 伊人在线',
    description:
      '了解伊人在线的网站定位、内容范围与联系方式。伊人在线专注精选电视剧与电影的内容展示与信息查询服务。',
    keywords: '关于伊人在线,影视平台介绍,联系方式,内容说明',
  });

  const features = [
    {
      icon: 'ri-information-line',
      title: '精选影视资讯',
      desc: '持续收录最新热门与经典影视作品信息，帮助你第一时间掌握影视动态。',
    },
    {
      icon: 'ri-filter-3-line',
      title: '多维筛选',
      desc: '支持按内容类型、地区、年份与热度、评分多条件筛选，快速定位想看的内容。',
    },
    {
      icon: 'ri-search-line',
      title: '快捷搜索',
      desc: '通过标题、主演或类型关键词，快速检索相关影视作品，结果一目了然。',
    },
    {
      icon: 'ri-heart-line',
      title: '本地收藏',
      desc: '收藏功能基于浏览器本地存储，仅作演示用途，方便你标记感兴趣的影视。',
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto px-4 py-12 md:px-10 md:py-16">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
              关于伊人在线
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              一个为影视爱好者打造的精选内容平台，让你更轻松地发现、筛选和了解想看的电视剧与电影。
            </p>
          </div>
        </section>

        <section className="mx-auto px-4 py-12 md:px-10 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-focus-3-line text-primary-600"></i>
                网站定位
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                伊人在线定位为影视内容的展示与信息查询平台，服务对象是喜爱影视娱乐、追求高清多样影视资源的普通大众与影迷。我们聚焦精选电视剧与电影两大品类，通过清晰的信息层级与卡片化的浏览方式，帮助用户高效地浏览、筛选与查询影视作品。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                需要说明的是，本站为内容展示与信息查询平台，<strong className="font-semibold text-foreground-900">不提供实际视频播放、下载或会员付费功能</strong>。页面中出现的播放入口仅为信息展示指引，相关功能均以演示说明为准。
              </p>

              <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-stack-line text-accent-600"></i>
                内容范围
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                内容涵盖最新热门影视与经典剧目，兼顾电影和电视剧两大品类。每部作品均包含名称、封面、类型、主演、导演、年份、地区、时长与剧情简介等信息，并提供同类型或同导演的相关推荐，便于发现更多感兴趣的内容。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category?type=%E7%94%B5%E8%A7%86%E5%89%A7"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-tv-2-line"></i>
                  浏览电视剧
                </Link>
                <Link
                  to="/category?type=%E7%94%B5%E5%BD%B1"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-film-line"></i>
                  浏览电影
                </Link>
              </div>
            </div>

            <aside className="space-y-4">
              <div className="rounded-lg bg-primary-50 p-6 ring-1 ring-primary-100">
                <h3 className="font-heading text-base font-bold text-foreground-950">数据一览</h3>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-600">收录作品</span>
                    <span className="font-semibold text-foreground-950">{movies.length} 部</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-600">电视剧</span>
                    <span className="font-semibold text-foreground-950">
                      {movies.filter((m) => m.type === '电视剧').length} 部
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-600">电影</span>
                    <span className="font-semibold text-foreground-950">
                      {movies.filter((m) => m.type === '电影').length} 部
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-background-100 p-6 ring-1 ring-background-200/70">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-mail-line text-primary-600"></i>
                  联系方式
                </h3>
                <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-customer-service-2-line text-foreground-400"></i>
                    客服邮箱：service@yiren-online.com
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-hand-heart-line text-foreground-400"></i>
                    内容合作：partner@yiren-online.com
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-time-line text-foreground-400"></i>
                    服务时间：每日 9:00 - 22:00
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-background-100/60">
          <div className="mx-auto px-4 py-12 md:px-10 md:py-16">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              我们提供什么
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="rounded-lg bg-background-50 p-5 ring-1 ring-background-200/70"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${f.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto px-4 py-12 md:px-10 md:py-16">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-question-line text-accent-600"></i>
            使用说明
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <span className="font-heading text-3xl font-extrabold text-primary-200">01</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">浏览与筛选</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                在首页或分类页浏览影视卡片，通过内容类型、地区、年份与排序条件，快速缩小范围。
              </p>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <span className="font-heading text-3xl font-extrabold text-primary-200">02</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">查看详情</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                点击任意影视卡片进入详情页，查看基本信息、剧情简介与相关推荐，并可通过收藏按钮标记喜欢的内容。
              </p>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
              <span className="font-heading text-3xl font-extrabold text-primary-200">03</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">搜索与收藏</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                使用搜索框输入关键词查找影视；收藏数据保存在浏览器本地，仅作演示，清除浏览器数据后会重置。
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}